import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { projectService } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';

const ProjectsPage = () => {
  const { checkApprovalStatus } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSwitching, setIsSwitching] = useState(null);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await projectService.getProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setIsCreating(true);
    setError(null);
    try {
      const result = await projectService.createProject(newProjectName.trim());
      setSuccessMessage(`Project "${result.name}" created successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setCreateModal(false);
      setNewProjectName('');

      // If first project, refresh token to get the new tenantId
      if (result.requires_refresh) {
        await checkApprovalStatus();
      }

      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSwitchProject = async (projectId) => {
    setIsSwitching(projectId);
    setError(null);
    try {
      await projectService.switchProject(projectId);
      // No token refresh needed - active project is stored in database
      setSuccessMessage('Switched to project successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to switch project');
    } finally {
      setIsSwitching(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-purple-500/20 text-purple-400',
      admin: 'bg-primary-500/20 text-primary-400',
      member: 'bg-dark-700 text-dark-400',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[role] || styles.member}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Projects</h1>
          <p className="text-dark-400 mt-1">Manage your projects and team members</p>
        </div>
        <button
          onClick={() => setCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Project
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-error-500/20 border border-error-500/50 rounded-lg text-error-400">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-success-500/20 border border-success-500/50 rounded-lg text-success-400">
          {successMessage}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      ) : projects.length === 0 ? (
        /* Empty State */
        <div className="card-dark p-12 text-center">
          <div className="w-16 h-16 bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-dark-200 mb-2">No projects yet</h3>
          <p className="text-dark-400 mb-6">Create your first project to start running simulations.</p>
          <button
            onClick={() => setCreateModal(true)}
            className="btn-primary"
          >
            Create your first project
          </button>
        </div>
      ) : (
        /* Projects Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`card-dark p-5 ${project.is_active ? 'ring-2 ring-primary-500' : ''}`}
            >
              {/* Project Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-dark-100 truncate">{project.name}</h3>
                    {project.is_active && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-500/20 text-primary-400">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="mt-1">{getRoleBadge(project.role)}</div>
                </div>
              </div>

              {/* Project Info */}
              <div className="space-y-2 text-sm text-dark-400 mb-4">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  <span>{project.members_count} member{project.members_count !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Link
                  to={`/app/projects/${project.id}`}
                  className="flex-1 btn-secondary text-center text-sm py-2"
                >
                  Manage
                </Link>
                {!project.is_active && (
                  <button
                    onClick={() => handleSwitchProject(project.id)}
                    disabled={isSwitching === project.id}
                    className="flex-1 btn-primary text-sm py-2 disabled:opacity-50"
                  >
                    {isSwitching === project.id ? 'Switching...' : 'Switch'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-dark-100 mb-4">Create New Project</h2>
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="input-dark w-full"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModal(false);
                    setNewProjectName('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newProjectName.trim() || isCreating}
                  className="btn-primary disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
